import type { ReactNode } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";

import { useSession } from "./session.tsx";
import {
  AccessDenied,
  ServerUnreachable,
  SessionPending,
  SessionProblem,
} from "./SessionScreens.tsx";
import { SESSION_TEXTS } from "./texts.ts";

/**
 * Lo que se dibuja en lugar de la aplicación mientras no se puede usar.
 *
 * La ruta ya ha intentado recuperar la sesión en `beforeLoad`; esto enseña
 * cómo quedó. Entrar exige `ccmas:global.view` (§10.2): tener sesión en
 * el SSO de lala-ms no es tenerla en Conti Club Más.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const { state, me } = session;

  // Volviendo al SSO porque la sesión caducó: la aplicación sigue a la vista
  // hasta que el navegador se vaya, sin parpadear a una pantalla de carga.
  const redirecting = state.status === "expired" && state.reason === undefined;

  if (state.status === "authenticated" || (redirecting && me !== null)) {
    if (!session.can(PERMISSIONS.access)) {
      return (
        <AccessDenied
          email={me?.email ?? ""}
          onSignOut={() => void session.signOut()}
        />
      );
    }
    return children;
  }

  /* No se llegó al servidor: no es falta de sesión, así que no se va al
     SSO. Se dice y se deja reintentar. */
  if (state.status === "unreachable") {
    return (
      <ServerUnreachable
        reason={state.reason}
        reference={state.reference}
        onRetry={session.retry}
        onSignIn={session.signIn}
      />
    );
  }

  if (
    (state.status === "anonymous" || state.status === "expired") &&
    state.reason !== undefined
  ) {
    return (
      <SessionProblem
        title={
          state.status === "expired"
            ? SESSION_TEXTS.sessionExpired
            : SESSION_TEXTS.signInFailed
        }
        reason={state.reason}
        onSignIn={session.signIn}
      />
    );
  }

  return <SessionPending>{SESSION_TEXTS.entering}</SessionPending>;
}
